import { JSONUtils } from '../../utility/json-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import {
  cloneListOfCloneable, isSameData, isSameList,
} from '../dto/data-model-utils.js';
import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData,
} from '../dto/data-model.js';
import {
  BusinessLevelMarketingSubscription,
} from './business-level-marketing/business-level-marketing-subscription.js';
import {
  BusinessLevelMarketingSubscriptionRequest,
} from './business-level-marketing/business-level-marketing-subscription-request.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { Dayjs } from 'dayjs';

type EncodedBusinessModuleRef = ReturnType<BusinessModuleRef['encode']>;

/**
 * Formerly known as BusinessLevelMarketingSubscriptionsInfo.
 */
export class MarketingConsent extends DataModel {
  static readonly TYPE_ID: string = 'abab352a36f2454689657c99bf739344';

  private constructor(
    public readonly accountId: string,
    public readonly isGuestAccount: boolean,
    public readonly creationDate: Dayjs,
    public readonly platformMarketingConsent: boolean,
    public readonly pendingSubscriptions: BusinessLevelMarketingSubscriptionRequest[],
    public readonly activeSubscriptions: BusinessLevelMarketingSubscription[],
  ) {
    super();

    const uniqueBusinessRefs = new Set<EncodedBusinessModuleRef>();
    for(const request of pendingSubscriptions) {
      const encodedBusinessRef = request.businessRef.encode();
      if(uniqueBusinessRefs.has(encodedBusinessRef)) {
        throw new Error(`Duplicate business ref found in pending subscriptions: ${encodedBusinessRef}`);
      }

      uniqueBusinessRefs.add(encodedBusinessRef);
    }

    for(const subscription of activeSubscriptions) {
      const encodedBusinessRef = subscription.businessRef.encode();
      if(uniqueBusinessRefs.has(encodedBusinessRef)) {
        throw new Error(`Duplicate business ref found in active subscriptions: ${encodedBusinessRef}`);
      }

      uniqueBusinessRefs.add(encodedBusinessRef);
    }
  }

  get uniqueId(): string {
    return this.accountId;
  }

  get isPending(): boolean {
    return this.pendingSubscriptions.length > 0;
  }

  get $typeId(): string {
    return MarketingConsent.TYPE_ID;
  }

  static create(args: {
    accountId: string,
    isGuestAccount: boolean,
    creationDate: Dayjs,
    platformMarketingConsent: boolean,
    pendingSubscriptions: BusinessLevelMarketingSubscriptionRequest[],
    activeSubscriptions: BusinessLevelMarketingSubscription[],
  }): MarketingConsent {
    return new MarketingConsent(
        args.accountId,
        args.isGuestAccount,
        args.creationDate,
        args.platformMarketingConsent,
        args.pendingSubscriptions,
        args.activeSubscriptions,
    );
  }

  static fresh(args: {
    accountId: string,
    isGuestAccount: boolean,
    creationDate: Dayjs,
  }): MarketingConsent {
    return this.create({
      accountId: args.accountId,
      isGuestAccount: args.isGuestAccount,
      creationDate: args.creationDate,
      platformMarketingConsent: false,
      pendingSubscriptions: [],
      activeSubscriptions: [],
    });
  }

  static fromMap(context: SerializationContext, map: SerializedData): MarketingConsent {
    return this.create({
      accountId: MapUtils.getString(map, 'accountId'),
      isGuestAccount: MapUtils.getBoolean(map, 'isGuestAccount'),
      creationDate: context.decodeDate(MapUtils.getNumber(map, 'creationDate')),
      platformMarketingConsent: MapUtils.getBoolean(map, 'platformMarketingConsent'),
      pendingSubscriptions: MapUtils.getList(map, 'pendingSubscriptions', (data) => context.fromData(JSONUtils.decodeRecord(data))),
      activeSubscriptions: MapUtils.getList(map, 'activeSubscriptions', (data) => context.fromData(JSONUtils.decodeRecord(data))),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      uniqueId: this.uniqueId,
      accountId: this.accountId,
      isGuestAccount: this.isGuestAccount,
      creationDate: context.encodeDate(this.creationDate),
      platformMarketingConsent: this.platformMarketingConsent,
      pendingSubscriptions: this.pendingSubscriptions.map((value) => context.toData(value)),
      activeSubscriptions: this.activeSubscriptions.map((value) => context.toData(value)),
    };
  }

  clone(): this {
    return this.copyWith();
  }

  copyWith({
    accountId = this.accountId,
    isGuestAccount = this.isGuestAccount,
    creationDate = this.creationDate,
    platformMarketingConsent = this.platformMarketingConsent,
    pendingSubscriptions = cloneListOfCloneable(this.pendingSubscriptions),
    activeSubscriptions = cloneListOfCloneable(this.activeSubscriptions),
  }: {
    accountId?: string,
    isGuestAccount?: boolean,
    creationDate?: Dayjs,
    platformMarketingConsent?: boolean,
    pendingSubscriptions?: BusinessLevelMarketingSubscriptionRequest[],
    activeSubscriptions?: BusinessLevelMarketingSubscription[],
  } = {}): this {
    return MarketingConsent.create({
      accountId,
      isGuestAccount,
      creationDate,
      platformMarketingConsent,
      pendingSubscriptions,
      activeSubscriptions,
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.accountId === this.accountId &&
           other.isGuestAccount === this.isGuestAccount &&
           other.creationDate === this.creationDate &&
           other.platformMarketingConsent === this.platformMarketingConsent &&
           isSameList(other.pendingSubscriptions, this.pendingSubscriptions, isSameData) &&
           isSameList(other.activeSubscriptions, this.activeSubscriptions, isSameData);
  }

  isSubscribedToBusiness(businessRef: BusinessModuleRef): boolean {
    return this.pendingSubscriptions.some((request) => isSameData(request.businessRef, businessRef)) ||
           this.activeSubscriptions.some((subscription) => isSameData(subscription.businessRef, businessRef));
  }

  pushRequest(request: BusinessLevelMarketingSubscriptionRequest): MarketingConsent {
    return this.copyWith({
      pendingSubscriptions: [...cloneListOfCloneable(this.pendingSubscriptions), request],
    });
  }

  private removeRequest(businessRef: BusinessModuleRef): MarketingConsent {
    return this.copyWith({
      pendingSubscriptions: cloneListOfCloneable(this.pendingSubscriptions)
          .filter((request) => !isSameData(request.businessRef, businessRef)),
    });
  }

  pushSubscription(subscription: BusinessLevelMarketingSubscription): MarketingConsent {
    return this.removeRequest(subscription.businessRef).copyWith({
      activeSubscriptions: [...cloneListOfCloneable(this.activeSubscriptions), subscription],
    });
  }

  static verifyType(order: DataModel): boolean {
    return order instanceof MarketingConsent;
  }

  static assertType(order: DataModel): order is MarketingConsent {
    return order instanceof MarketingConsent;
  }
}

export class MarketingConsentBuilder extends DataModelBuilder<MarketingConsent> {
  build(context: SerializationContext, map: SerializedData): MarketingConsent {
    return MarketingConsent.fromMap(context, map);
  }

  optimizeForQuery(context: SerializationContext, model: MarketingConsent): SerializedData | null {
    return {
      isPending: model.isPending,
    };
  }
}
